window.addEventListener('load', () => {

  document.querySelectorAll('.hero-slider').forEach(slider => {

    const slides = slider.querySelectorAll('.hero-slide');
    if (slides.length <= 1) {
      // Still init parallax even for single slides
      initParallax(slider);
      initBackgroundParallax(slider);
      return;
    }

    let current   = 0;
    let animating = false;
    // Enable/disable infinite looping
    const infiniteLoop = true;

    const nextBtn  = slider.querySelector('.hero-slider__arrow--next');
    const prevBtn  = slider.querySelector('.hero-slider__arrow--prev');
    const autoplay = slider.dataset.autoplay !== 'false';
    const speed    = (parseInt(slider.dataset.autoplaySpeed, 10) || 6) * 1000;

    /* ------------------------------------------------------------------
       ARROW STATE
    ------------------------------------------------------------------ */

    function setArrowState(btn, disabled) {
      if (!btn) return;
      btn.classList.toggle('is-disabled', disabled);
      if ('disabled' in btn) btn.disabled = disabled;
    }

    function updateArrows(index) {
      if (infiniteLoop) {
        setArrowState(prevBtn, false);
        setArrowState(nextBtn, false);
        return;
      }

      if (autoplay) return;

      setArrowState(prevBtn, index === 0);
      setArrowState(nextBtn, index === slides.length - 1);
    }

    /* ------------------------------------------------------------------
       SLIDE TRANSITION
       direction: 1 = forward (slide left), -1 = backward (slide right)
    ------------------------------------------------------------------ */

    function showSlide(index, direction) {

      if (animating || index === current) return;
      animating = true;

      direction = direction ?? (index > current ? 1 : -1);

      const outgoing = slides[current];
      const incoming = slides[index];

      // Offset direction values
      const outEnd  = direction === 1 ? '-100%' : '100%';
      const inStart = direction === 1 ?  '100%' : '-100%';

      // Prepare incoming slide
      incoming.style.transform = `translateX(${inStart})`;
      incoming.style.opacity = '0';
      incoming.style.visibility = 'visible';
      incoming.classList.add('is-transitioning');

      // Recompute parallax right away — without this, the incoming
      // slide's background/mockup only ever gets positioned by a real
      // scroll event (or the transitionend dispatch below), so it sits
      // unshifted at translateY(0) for the entire crossfade and only
      // snaps into the correct position once the transition finishes.
      window.dispatchEvent(new Event('scroll'));

      // Force browser to paint before animating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {

          outgoing.style.transition =
            'transform 0.55s cubic-bezier(0.76,0,0.24,1), opacity 0.55s ease';

          incoming.style.transition =
            'transform 0.55s cubic-bezier(0.76,0,0.24,1), opacity 0.55s ease';

          outgoing.style.transform = `translateX(${outEnd})`;
          outgoing.style.opacity = '0';

          incoming.style.transform = 'translateX(0)';
          incoming.style.opacity = '1';

        });
      });

      // Clean up after transition
      incoming.addEventListener('transitionend', function onEnd(e) {
        if (e.propertyName !== 'transform') return;
        incoming.removeEventListener('transitionend', onEnd);

        // Reset outgoing slide
        outgoing.classList.remove('is-active');
        outgoing.style.transition  = '';
        outgoing.style.transform   = '';
        outgoing.style.opacity     = '';
        outgoing.style.visibility  = '';

        // Activate incoming slide
        incoming.classList.add('is-active');
        incoming.classList.remove('is-transitioning');
        incoming.style.transition  = '';
        incoming.style.transform   = '';
        incoming.style.opacity     = '';

        current   = index;
        animating = false;

        updateArrows(current);
        updateParallaxForSlide(slider, slides[current]);
      }, { once: false });
    }

    /* ------------------------------------------------------------------
       ARROW CLICKS
    ------------------------------------------------------------------ */

    nextBtn?.addEventListener('click', () => {
      let next = current + 1;

      if (next >= slides.length) {
        if (infiniteLoop) {
          next = 0;
        } else {
          return;
        }
      }
      showSlide(next, 1);
      resetAutoplay();
    });

    prevBtn?.addEventListener('click', () => {
      let prev = current - 1;

      if (prev < 0) {
        if (infiniteLoop) {
          prev = slides.length - 1;
        } else {
          return;
        }
      }
      showSlide(prev, -1);
      resetAutoplay();
    });

    /* ------------------------------------------------------------------
       AUTOPLAY
    ------------------------------------------------------------------ */

    let autoplayTimer = null;

    function startAutoplay() {
      if (!autoplay) return;
      autoplayTimer = setInterval(() => {
        let next = current + 1;
        if (next >= slides.length) next = 0;
        showSlide(next, 1);
      }, speed);
    }

    function resetAutoplay() {
      if (!autoplay) return;
      clearInterval(autoplayTimer);
      startAutoplay();
    }

    /* ------------------------------------------------------------------
       SWIPE HELPER — shared by touch and mouse drag
    ------------------------------------------------------------------ */

    const SWIPE_THRESHOLD = 50;

    function handleSwipeEnd(dx, dy) {
      // Ignore if vertical movement is dominant
      if (Math.abs(dy) > Math.abs(dx)) return;
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;

      if (dx < 0) {
        // Swiped left → next
        let next = current + 1;

        if (next >= slides.length) {
          if (infiniteLoop) {
            next = 0;
          } else {
            return;
          }
        }
        showSlide(next, 1);
        resetAutoplay();
      } else {
        // Swiped right → prev
        let prev = current - 1;

        if (prev < 0) {
          if (infiniteLoop) {
            prev = slides.length - 1;
          } else {
            return;
          }
        }
        showSlide(prev, -1);
        resetAutoplay();
      }
    }

    /* ------------------------------------------------------------------
       TOUCH SWIPE (mobile)
    ------------------------------------------------------------------ */

    let touchStartX = 0;
    let touchStartY = 0;
    let isTouching  = false;

    slider.addEventListener('touchstart', e => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isTouching  = true;
    }, { passive: true });

    slider.addEventListener('touchmove', e => {
      if (!isTouching) return;
      const dx = Math.abs(e.touches[0].clientX - touchStartX);
      const dy = Math.abs(e.touches[0].clientY - touchStartY);
      // Prevent page scroll only when clearly a horizontal swipe
      if (dx > dy && dx > 10) e.preventDefault();
    }, { passive: false });

    slider.addEventListener('touchend', e => {
      if (!isTouching) return;
      isTouching = false;
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      handleSwipeEnd(dx, dy);
    }, { passive: true });

    /* ------------------------------------------------------------------
       MOUSE DRAG SWIPE (desktop)
    ------------------------------------------------------------------ */

    let mouseStartX  = 0;
    let mouseStartY  = 0;
    let isDragging   = false;
    let didDrag      = false; // true once mouse moved past a small threshold

    slider.addEventListener('mousedown', e => {
      // Only primary button
      if (e.button !== 0) return;
      mouseStartX = e.clientX;
      mouseStartY = e.clientY;
      isDragging  = true;
      didDrag     = false;
      slider.style.cursor = 'grabbing';
      // Prevent text/image selection while dragging
      e.preventDefault();
    });

    window.addEventListener('mousemove', e => {
      if (!isDragging) return;
      const dx = Math.abs(e.clientX - mouseStartX);
      if (dx > 5) didDrag = true;
    });

    window.addEventListener('mouseup', e => {
      if (!isDragging) return;
      isDragging          = false;
      slider.style.cursor = '';

      if (!didDrag) return; // was just a click, not a drag

      const dx = e.clientX - mouseStartX;
      const dy = e.clientY - mouseStartY;
      handleSwipeEnd(dx, dy);
    });

    // If the mouse leaves the window mid-drag, cancel cleanly
    window.addEventListener('mouseleave', () => {
      if (!isDragging) return;
      isDragging          = false;
      slider.style.cursor = '';
    });

    // Prevent ghost drag of slide images / links
    slider.addEventListener('dragstart', e => e.preventDefault());

    /* ------------------------------------------------------------------
       INIT
    ------------------------------------------------------------------ */

    // Position all slides absolutely so transitions work
    slides.forEach((slide, i) => {
      if (i !== 0) {
        slide.style.visibility = 'hidden';
      }
    });

    updateArrows(0);
    startAutoplay();
    initParallax(slider);
    initBackgroundParallax(slider);

  }); // end forEach slider

  /* ====================================================================
     PARALLAX MOCKUP
     Reads --parallax-speed from each slide (set via inline style in Liquid)
     and translates the .hero-slide__mockup on scroll.
  ==================================================================== */

  function initParallax(slider) {
    const slides = slider.querySelectorAll('.hero-slide');

    slides.forEach(slide => {

      const isMobile = window.innerWidth <= 767;

      const enabled = isMobile
        ? slide.dataset.parallaxMobile === 'true'
        : slide.dataset.parallaxDesktop === 'true';

      if (!enabled) return;

      const mockup = slide.querySelector('.hero-slide__mockup');
      if (!mockup) return;

      const speed = parseFloat(
        getComputedStyle(slide).getPropertyValue('--parallax-speed') || '0.4'
      );

      // Cache slide rect; refresh on resize
      let sliderRect = slider.getBoundingClientRect();

      const resizeObserver = new ResizeObserver(() => {
        sliderRect = slider.getBoundingClientRect();
      });
      resizeObserver.observe(slider);

      function applyParallax() {
        // Only animate the active slide
        if (!slide.classList.contains('is-active') && !slide.classList.contains('is-transitioning')) return;

        const scrollY      = window.scrollY;
        const sliderTop    = slider.offsetTop;
        const sliderHeight = slider.offsetHeight;
        const winH         = window.innerHeight;

        // Parallax starts when the slider top is 20% below the window top.
        // That trigger point in page coords is: scrollY + winH * 0.20
        // Before that point rawOffset stays 0 (mockup doesn't move).
        const triggerScrollY = isMobile ? sliderTop - winH * 0.15 : sliderTop - winH * 0;
        const scrollPast     = scrollY - triggerScrollY;
        const rawOffset      = scrollPast > 0 ? scrollPast * speed : 0;

        // Clamp upward travel so the mockup's bottom edge never rises
        // above the slider's bottom edge.
        //
        // Reset transform first so getBoundingClientRect reflects the
        // natural (translateY = 0) position, then reapply immediately.
        mockup.style.transform = '';
        const mockupRect = mockup.getBoundingClientRect();
        const sliderRect = slider.getBoundingClientRect();

        // gap = how far mockup bottom is below slider bottom at rest.
        // That distance is exactly how far the mockup can travel upward
        // before its bottom edge exits the slider.
        const gap    = mockupRect.bottom - sliderRect.bottom;
        const maxUp  = gap > 0 ? gap : 0;
        const offset = Math.max(rawOffset, -maxUp);

        mockup.style.transform = `translateY(${offset}px)`;
      }

      window.addEventListener('scroll', applyParallax, { passive: true });
      applyParallax();

      // Browsers restore scroll position on reload (history.scrollRestoration
      // defaults to "auto"), sometimes slightly AFTER this script's initial
      // synchronous call above already ran against scroll position 0 — that
      // race is exactly what causes a visible jump right after reload. Re-run
      // once on the next frame and once a beat later to catch wherever the
      // browser actually settles, on top of whatever the real scroll ends up.
      requestAnimationFrame(applyParallax);
      window.setTimeout(applyParallax, 150);
    });
  }

  function updateParallaxForSlide(slider, activeSlide) {
    // Trigger a scroll event so the newly active slide's mockup gets positioned
    window.dispatchEvent(new Event('scroll'));
  }

  /* ====================================================================
     BACKGROUND IMAGE PARALLAX
     Same trigger/speed math as the mockup parallax above, but applied to
     the oversized .hero-slide__image--parallax element instead — moves
     slower than the content on top of it by using a lower speed value
     (set per-slide via bg_parallax_speed). Clamp range is symmetric
     (image is centered via CSS inset:-12% 0) rather than the mockup's
     bottom-edge-only clamp.
  ==================================================================== */

  function initBackgroundParallax(slider) {
    const slides = slider.querySelectorAll('.hero-slide');

    slides.forEach(slide => {

      const isMobile = window.innerWidth <= 767;

      const enabled = isMobile
        ? slide.dataset.bgParallaxMobile === 'true'
        : slide.dataset.bgParallaxDesktop === 'true';

      if (!enabled) return;

      const img = slide.querySelector('.hero-slide__image--parallax');
      if (!img) return;

      const speed = parseFloat(
        getComputedStyle(slide).getPropertyValue('--bg-parallax-speed') || '0.15'
      );

      function applyParallax() {
        // Only animate the active slide
        if (!slide.classList.contains('is-active') && !slide.classList.contains('is-transitioning')) return;

        const scrollY   = window.scrollY;
        const sliderTop = slider.offsetTop;
        const winH      = window.innerHeight;

        const triggerScrollY = isMobile ? sliderTop - winH * 0.2 : sliderTop - winH * 0;
        const scrollPast     = scrollY - triggerScrollY;
        const rawOffset      = scrollPast > 0 ? scrollPast * speed : 0;

        // Reset transform first so getBoundingClientRect reflects the
        // natural (translateY = 0) position, then clamp symmetrically —
        // the image is centered via CSS inset, so it can travel equally
        // up or down before its edge reaches the slide's edge.
        img.style.transform = '';
        const imgRect   = img.getBoundingClientRect();
        const slideRect = slide.getBoundingClientRect();
        const maxShift  = Math.max(0, (imgRect.height - slideRect.height) / 2);
        const offset    = Math.min(rawOffset, maxShift);

        img.style.transform = `translateY(${offset}px)`;
      }

      window.addEventListener('scroll', applyParallax, { passive: true });
      applyParallax();
      requestAnimationFrame(applyParallax);
      window.setTimeout(applyParallax, 150);
    });
  }

});